import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    UsersIcon, UserPlusIcon, MagnifyingGlassIcon,
    BriefcaseIcon, AcademicCapIcon, CalendarDaysIcon,
    ArrowUpIcon, ArrowDownIcon, XMarkIcon
} from '@heroicons/react/24/outline';

const initialEmployeesData = [
    { id: 1, name: "Rahul Kumar", department: "HR", role: "Director", status: "Active", initials: "RK", joiningDate: '2015-03-10' },
    { id: 2, name: "Anjali Sharma", department: "IT", role: "Senior Developer", status: "Active", initials: "AS", joiningDate: '2018-05-22' },
    { id: 3, name: "Sanjay Reddy", department: "Finance", role: "Lead Analyst", status: "Inactive", initials: "SR", joiningDate: '2016-11-01' },
    { id: 4, name: "Priya Patel", department: "Marketing", role: "Head of Marketing", status: "Active", initials: "PP", joiningDate: '2020-09-15' },
    { id: 5, name: "Vikram Singh", department: "Sales", role: "Senior Representative", status: "Active", initials: "VS", joiningDate: '2021-01-20' },
    { id: 6, name: "Deepa Gupta", department: "HR", role: "Recruitment Specialist", status: "Active", initials: "DG", joiningDate: '2022-04-05' },
    { id: 7, name: "Manish Joshi", department: "Legal", role: "Corporate Counsel", status: "Inactive", initials: "MJ", joiningDate: '2019-07-12' },
];

const getYearsOfExperience = (joiningDate) => {
    const start = new Date(joiningDate);
    const diff = Date.now() - start.getTime();
    const years = Math.floor(diff / (1000 * 60 * 60 * 24 * 365.25));
    return years;
};

const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
};

const Employees = () => {
    const [employeesData, setEmployeesData] = useState(initialEmployeesData);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedDepartment, setSelectedDepartment] = useState('All');
    const [sortBy, setSortBy] = useState('name');
    const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [newEmployee, setNewEmployee] = useState({ name: '', department: '', role: '', joiningDate: '' });

    const departments = useMemo(() => ['All', ...new Set(employeesData.map(emp => emp.department))], [employeesData]);

    const sortedAndFilteredEmployees = useMemo(() => {
        let filtered = employeesData.filter(employee => {
            const matchesSearch = employee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                 employee.department.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                 employee.role.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesDepartment = selectedDepartment === 'All' || employee.department === selectedDepartment;
            return matchesSearch && matchesDepartment;
        });

        filtered.sort((a, b) => {
            const aValue = a[sortBy];
            const bValue = b[sortBy];

            if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
            return 0;
        });

        return filtered;
    }, [employeesData, searchTerm, selectedDepartment, sortBy, sortOrder]);

    const activeEmployees = employeesData.filter(emp => emp.status === 'Active').length;
    const totalEmployees = employeesData.length;
    const activePercentage = totalEmployees > 0 ? ((activeEmployees / totalEmployees) * 100).toFixed(0) : 0;

    const getStatusColor = (status) => {
        switch (status) {
            case 'Active':
                return 'bg-green-100 text-green-800';
            case 'Inactive':
                return 'bg-red-100 text-red-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const handleSort = (key) => {
        if (sortBy === key) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(key);
            setSortOrder('asc');
        }
    };

    const handleNewEmployeeChange = (e) => {
        const { name, value } = e.target;
        setNewEmployee(prevState => ({ ...prevState, [name]: value }));
    };

    const handleSaveNewEmployee = (e) => {
        e.preventDefault();
        if (!newEmployee.name || !newEmployee.department || !newEmployee.role || !newEmployee.joiningDate) {
            alert('Please fill in all employee details.');
            return;
        }

        const employeeToAdd = {
            id: employeesData.length + 1,
            ...newEmployee,
            status: 'Active',
            initials: getInitials(newEmployee.name),
        };

        setEmployeesData(prevData => [...prevData, employeeToAdd]);
        setNewEmployee({ name: '', department: '', role: '', joiningDate: '' });
        setIsAddModalOpen(false);
    };

    return (
        <div className="min-h-screen p-8 bg-gradient-to-br from-blue-50 to-indigo-100 font-sans text-gray-800">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="p-8 space-y-10 rounded-3xl shadow-xl bg-white"
            >
                <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight">
                    Human Resources Dashboard
                </h1>

                {/* --- */}

                {/* 🔹 Professional Stats and Actions */}
                <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <motion.div
                        className="p-6 bg-white rounded-2xl shadow-lg border border-gray-200"
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.4 }}
                    >
                        <div className="flex items-center space-x-4">
                            <div className="p-3 rounded-full bg-blue-100">
                                <UsersIcon className="h-8 w-8 text-blue-600" />
                            </div>
                            <div>
                                <p className="text-sm font-medium text-gray-500">Total Employees</p>
                                <h3 className="text-3xl font-bold mt-1 text-gray-900">{totalEmployees}</h3>
                            </div>
                        </div>
                    </motion.div>

                    <motion.div
                        className="p-6 bg-white rounded-2xl shadow-lg border border-gray-200"
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.4, delay: 0.1 }}
                    >
                        <div className="flex items-center space-x-4">
                            <div className="p-3 rounded-full bg-green-100">
                                <UsersIcon className="h-8 w-8 text-green-600" />
                            </div>
                            <div>
                                <p className="text-sm font-medium text-gray-500">Active Employees</p>
                                <h3 className="text-3xl font-bold mt-1 text-gray-900">{activeEmployees}</h3>
                            </div>
                        </div>
                    </motion.div>

                    <motion.div
                        className="p-6 bg-white rounded-2xl shadow-lg border border-gray-200"
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.4, delay: 0.2 }}
                    >
                        <div className="flex items-center space-x-4">
                            <div className="p-3 rounded-full bg-indigo-100">
                                <BriefcaseIcon className="h-8 w-8 text-indigo-600" />
                            </div>
                            <div>
                                <p className="text-sm font-medium text-gray-500">Retention Rate</p>
                                <h3 className="text-3xl font-bold mt-1 text-gray-900">{activePercentage}%</h3>
                            </div>
                        </div>
                    </motion.div>

                    <motion.button
                        onClick={() => setIsAddModalOpen(true)}
                        className="flex items-center justify-center p-6 rounded-2xl shadow-lg text-white font-semibold bg-blue-600 hover:bg-blue-700 transition-colors duration-200"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, delay: 0.3 }}
                    >
                        <UserPlusIcon className="h-6 w-6 mr-2" />
                        <span className="text-lg">Add New Employee</span>
                    </motion.button>
                </section>

                {/* --- */}

                {/* 🔹 Employee Directory Controls */}
                <section className="space-y-6">
                    <div className="flex flex-col md:flex-row items-center gap-4">
                        <div className="relative flex-1 w-full">
                            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                                type="text"
                                className="block w-full rounded-full border-0 py-3 pl-10 pr-4 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-500 text-sm transition-colors duration-200"
                                placeholder="Search employees by name, department, or role..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                            {departments.map(dept => (
                                <button
                                    key={dept}
                                    onClick={() => setSelectedDepartment(dept)}
                                    className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors duration-200 ${
                                        selectedDepartment === dept
                                            ? 'bg-blue-600 text-white shadow-md'
                                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                    }`}
                                >
                                    {dept}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="flex items-center text-sm text-gray-600 space-x-2">
                        <span>Sort by:</span>
                        <button
                            onClick={() => handleSort('name')}
                            className={`flex items-center px-3 py-1 rounded-full hover:bg-gray-200 transition-colors ${sortBy === 'name' ? 'bg-gray-300 font-semibold' : ''}`}
                        >
                            Name {sortBy === 'name' && (sortOrder === 'asc' ? <ArrowUpIcon className="h-4 w-4 ml-1" /> : <ArrowDownIcon className="h-4 w-4 ml-1" />)}
                        </button>
                        <button
                            onClick={() => handleSort('joiningDate')}
                            className={`flex items-center px-3 py-1 rounded-full hover:bg-gray-200 transition-colors ${sortBy === 'joiningDate' ? 'bg-gray-300 font-semibold' : ''}`}
                        >
                            Seniority {sortBy === 'joiningDate' && (sortOrder === 'asc' ? <ArrowUpIcon className="h-4 w-4 ml-1" /> : <ArrowDownIcon className="h-4 w-4 ml-1" />)}
                        </button>
                    </div>

                    {/* --- */}

                    {/* 🔹 Employee Cards Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        <AnimatePresence>
                            {sortedAndFilteredEmployees.length > 0 ? (
                                sortedAndFilteredEmployees.map((employee, index) => (
                                    <motion.div
                                        key={employee.id}
                                        className="bg-white rounded-2xl shadow-md p-6 flex flex-col items-center text-center space-y-4 transition-transform transform hover:scale-[1.03] duration-300 border border-gray-200"
                                        initial={{ opacity: 0, scale: 0.95 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.95 }}
                                        transition={{ duration: 0.3, delay: index * 0.05 }}
                                        layout
                                    >
                                        <div className="w-24 h-24 rounded-full flex items-center justify-center text-2xl font-bold text-white bg-blue-600 border-4 border-gray-300 shadow-md">
                                            {employee.initials}
                                        </div>
                                        <div className="space-y-1">
                                            <h3 className="text-xl font-bold text-gray-900">{employee.name}</h3>
                                            <p className="text-sm font-medium text-gray-600 flex items-center justify-center">
                                                <BriefcaseIcon className="h-4 w-4 mr-1 text-gray-500" />
                                                {employee.role}
                                            </p>
                                            <p className="text-sm font-medium text-gray-600 flex items-center justify-center">
                                                <AcademicCapIcon className="h-4 w-4 mr-1 text-gray-500" />
                                                {employee.department}
                                            </p>
                                            <p className="text-xs text-gray-500 font-normal flex items-center justify-center">
                                                <CalendarDaysIcon className="h-4 w-4 mr-1 text-gray-500" />
                                                <span className="text-gray-900 font-semibold mr-1">{getYearsOfExperience(employee.joiningDate)}+</span> years of experience
                                            </p>
                                        </div>
                                        <span className={`px-3 py-1 text-xs font-semibold rounded-full uppercase ${getStatusColor(employee.status)}`}>
                                            {employee.status}
                                        </span>
                                    </motion.div>
                                ))
                            ) : (
                                <div className="lg:col-span-3 text-center py-12 text-gray-500">
                                    <p className="text-lg">No employees found matching your criteria.</p>
                                </div>
                            )}
                        </AnimatePresence>
                    </div>
                </section>
            </motion.div>

            {/* --- */}

            {/* New Employee Modal */}
            <AnimatePresence>
                {isAddModalOpen && (
                    <motion.div
                        className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-40 backdrop-blur-sm"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <motion.div
                            className="bg-white p-8 rounded-2xl shadow-xl w-full max-w-lg mx-4"
                            initial={{ scale: 0.9, y: 50 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.9, y: 50 }}
                            transition={{ type: "spring", stiffness: 200, damping: 25 }}
                        >
                            <div className="flex justify-between items-start mb-6">
                                <h2 className="text-2xl font-bold text-gray-900">Add New Employee</h2>
                                <button onClick={() => setIsAddModalOpen(false)} className="p-2 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors">
                                    <XMarkIcon className="h-6 w-6" />
                                </button>
                            </div>
                            <form onSubmit={handleSaveNewEmployee} className="space-y-4">
                                <div>
                                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">Full Name</label>
                                    <input
                                        type="text"
                                        name="name"
                                        id="name"
                                        value={newEmployee.name}
                                        onChange={handleNewEmployeeChange}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2"
                                        required
                                    />
                                </div>
                                <div>
                                    <label htmlFor="department" className="block text-sm font-medium text-gray-700">Department</label>
                                    <input
                                        type="text"
                                        name="department"
                                        id="department"
                                        value={newEmployee.department}
                                        onChange={handleNewEmployeeChange}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2"
                                        required
                                    />
                                </div>
                                <div>
                                    <label htmlFor="role" className="block text-sm font-medium text-gray-700">Role</label>
                                    <input
                                        type="text"
                                        name="role"
                                        id="role"
                                        value={newEmployee.role}
                                        onChange={handleNewEmployeeChange}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2"
                                        required
                                    />
                                </div>
                                <div>
                                    <label htmlFor="joiningDate" className="block text-sm font-medium text-gray-700">Joining Date</label>
                                    <input
                                        type="date"
                                        name="joiningDate"
                                        id="joiningDate"
                                        value={newEmployee.joiningDate}
                                        onChange={handleNewEmployeeChange}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2"
                                        required
                                    />
                                </div>
                                <div className="flex justify-end space-x-3 mt-6">
                                    <button
                                        type="button"
                                        onClick={() => setIsAddModalOpen(false)}
                                        className="px-4 py-2 text-sm font-semibold rounded-md text-gray-700 bg-gray-200 hover:bg-gray-300 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-4 py-2 text-sm font-semibold rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors"
                                    >
                                        Save Employee
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default Employees;